import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { runDirector, listCampaigns, getCampaign, generateDeliverableImage, generateAllAssets } from "@/lib/api";
import { CAMPAIGN_TEMPLATES } from "@/lib/marketingData";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Strategy, Sparkle, PaperPlaneTilt, CalendarBlank, Target, Users, CurrencyDollar,
  Hash, Globe, Image as ImageIcon, FilmSlate, Copy, EnvelopeSimple, ShoppingBag, ChatText, CheckCircle, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EXAMPLES = [
  "Launch my Dark Roast next Friday",
  "Promote my Summer Blend this weekend",
  "Create a Father's Day campaign",
  "Run a 15% sale next week",
  "Promote free shipping this month",
  "Create a product launch campaign",
];
const ICON = { caption: ChatText, product_copy: ShoppingBag, email: EnvelopeSimple, hashtags: Hash, landing_page: Globe, image: ImageIcon, video: FilmSlate };

export default function MarketingDirectorPage() {
  const { activeId, activeClient } = useClient();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [command, setCommand] = useState("");
  const [active, setActive] = useState(null); // {campaign, deliverables}
  const autoRanRef = React.useRef(false);

  const campaignsQ = useQuery({ queryKey: ["campaigns", activeId], queryFn: () => listCampaigns(activeId), enabled: !!activeId });

  const run = useMutation({
    mutationFn: (cmd) => runDirector(activeId, (cmd ?? command)),
    onSuccess: (d) => { setActive(d); setCommand(""); qc.invalidateQueries({ queryKey: ["campaigns", activeId] }); toast.success("Campaign drafted & routed to the Approval Queue"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not generate campaign — try again", { duration: 9000 }),
  });

  // One-Click Command Center: arrive with ?prompt= → prefill AND auto-run the Director.
  React.useEffect(() => {
    const p = searchParams.get("prompt");
    if (p && activeId && !autoRanRef.current) {
      autoRanRef.current = true;
      setCommand(p);
      setSearchParams({}, { replace: true });
      toast.info("Building your campaign…");
      run.mutate(p);
    }
  }, [activeId, searchParams]); // eslint-disable-line
  const openCampaign = useMutation({ mutationFn: (id) => getCampaign(id), onSuccess: setActive });
  const genImg = useMutation({
    mutationFn: ({ cid, did, force }) => generateDeliverableImage(cid, did, force),
    onSuccess: () => { if (active) openCampaign.mutate(active.campaign.id); toast.success("Image generated → in Approval Queue"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Image generation failed"),
  });
  const genAll = useMutation({
    mutationFn: (cid) => generateAllAssets(cid),
    onSuccess: (d) => { if (active) openCampaign.mutate(active.campaign.id); toast.success(`Generated ${d.generated} image(s)${d.failed ? `, ${d.failed} failed` : ""} → Approval Queue`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Batch generation failed"),
  });

  const c = active?.campaign;
  const dels = active?.deliverables || [];
  const byType = (t) => dels.filter((d) => d.deliverable_type === t);

  return (
    <PageContainer>
      <PageHeader eyebrow="AI Marketing Director" title="Command your marketing"
        subtitle={`Type one command. The Vozelto AI Director drafts the full campaign for ${activeClient?.name || "your workspace"} — copy, email, schedule, images & video concepts — and routes everything to the Approval Queue. Nothing publishes automatically.`} />

      <div className="card-matte p-5 mb-5">
        <div className="flex items-center gap-2 mb-2"><Strategy size={18} className="brand-text" weight="fill" /><span className="font-head font-semibold">Give a command</span></div>
        <Textarea data-testid="director-command" value={command} onChange={(e) => setCommand(e.target.value)} rows={2} className="bg-[#10101A] border-white/10" placeholder="e.g. Launch my Dark Roast next Friday" />
        <div className="flex flex-wrap gap-2 mt-3">
          {EXAMPLES.map((ex) => (
            <button key={ex} data-testid={`example-${ex.slice(0, 10).toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setCommand(ex)} className="text-xs px-2.5 py-1.5 rounded-lg border border-white/8 text-[#9a9a9a] hover:text-white hover:border-white/20">{ex}</button>
          ))}
        </div>
        <div className="mt-3">
          <div className="label-caps mb-1.5">Campaign templates</div>
          <div className="flex flex-wrap gap-1.5">
            {CAMPAIGN_TEMPLATES.map((t) => (
              <button key={t.l} data-testid={`template-${t.l.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setCommand(t.p)}
                className="text-[11px] px-2 py-1 rounded-md bg-[#6B3DFF]/8 border border-[#6B3DFF]/20 text-[#A56CFF] hover:bg-[#6B3DFF]/15">{t.l}</button>
            ))}
          </div>
        </div>
        <Button data-testid="director-run-btn" onClick={() => command.trim() ? run.mutate(command) : toast.error("Type a command first")} disabled={run.isPending} className="mt-3 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
          <Sparkle size={16} className={`mr-1.5 ${run.isPending ? "ai-thinking" : ""}`} weight="fill" /> {run.isPending ? "Building campaign… (~20-40s)" : "Generate campaign"}
        </Button>
      </div>

      {c && (
        <div className="card-matte p-5 mb-5" data-testid="campaign-result">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div><div className="font-head text-xl font-semibold">{c.name}</div><div className="text-xs text-[#7a7a7a]">"{c.command}"</div></div>
            <span className="text-[11px] px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 flex items-center gap-1"><CheckCircle size={12} weight="fill" /> {dels.filter((d) => d.status === "pending").length} items in Approval Queue</span>
            {!!byType("image").length && (
              <Button data-testid="gen-all-btn" size="sm" onClick={() => genAll.mutate(c.id)} disabled={genAll.isPending}
                className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
                <Sparkle size={14} className={`mr-1.5 ${genAll.isPending ? "ai-thinking" : ""}`} weight="fill" />
                {genAll.isPending ? "Generating all…" : "Generate all images"}
              </Button>
            )}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-4">
            {[[Target, "Objective", c.objective], [Users, "Audience", c.audience], [CurrencyDollar, "Budget", c.budget], [Globe, "Landing page", c.landing_page]].map(([Ic, l, v]) => (
              <div key={l} className="bg-white/[0.03] border border-white/8 rounded-lg px-3 py-2"><div className="text-[#7a7a7a] label-caps flex items-center gap-1"><Ic size={12} /> {l}</div><div className="text-xs text-[#cfcfcf] mt-1 line-clamp-3">{v || "—"}</div></div>
            ))}
          </div>

          {!!(c.hashtags || []).length && <div className="flex flex-wrap gap-1.5 mt-3">{c.hashtags.map((h, i) => <span key={i} className="text-xs brand-text bg-[#6B3DFF]/10 px-2 py-0.5 rounded">{h}</span>)}</div>}

          {/* Schedule */}
          {!!(c.schedule || []).length && (
            <div className="mt-4" data-testid="campaign-schedule">
              <div className="font-head font-semibold text-sm flex items-center gap-1.5 mb-2"><CalendarBlank size={15} className="brand-text" /> 7-Day Schedule</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {c.schedule.map((s, i) => (
                  <div key={i} className="bg-white/[0.03] border border-white/8 rounded-lg px-3 py-2 text-xs">
                    <div className="brand-text font-semibold">{s.day} · {s.time} · {s.platform}</div>
                    <div className="text-[#cfcfcf] mt-0.5">{s.content}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Images */}
          {!!byType("image").length && (
            <div className="mt-4"><div className="font-head font-semibold text-sm flex items-center gap-1.5 mb-2"><ImageIcon size={15} className="brand-text" /> Images</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {byType("image").map((d) => (
                  <div key={d.id} className="card-matte p-2" data-testid={`del-image-${d.id}`}>
                    <div className="rounded-lg overflow-hidden bg-[#0a0a0a] border border-white/8 h-28 flex items-center justify-center mb-2">
                      {d.image_path ? <img src={`${BACKEND}${d.image_path}`} alt={d.title} className="h-full w-full object-cover" /> : <ImageIcon size={26} className="text-[#3a3a3a]" />}
                    </div>
                    <div className="text-xs text-[#cfcfcf] truncate">{d.title}</div>
                    <Button data-testid={`gen-${d.id}`} size="sm" onClick={() => genImg.mutate({ cid: c.id, did: d.id, force: !!d.image_path })} disabled={genImg.isPending} className="w-full mt-1.5 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white text-xs">
                      <Sparkle size={12} className="mr-1" /> {d.image_path ? "Regenerate" : "Generate"}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Videos (coming soon) */}
          {!!byType("video").length && (
            <div className="mt-4"><div className="font-head font-semibold text-sm flex items-center gap-1.5 mb-2"><FilmSlate size={15} className="brand-text" /> Videos</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {byType("video").map((d) => (
                  <div key={d.id} className="card-matte p-3 opacity-80" data-testid={`del-video-${d.id}`}>
                    <div className="flex items-center justify-between"><span className="text-xs text-[#cfcfcf]">{d.title}</span><span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[#9a9a9a] border border-white/10 flex items-center gap-1"><Clock size={10} /> Coming soon</span></div>
                    <div className="text-[11px] text-[#7a7a7a] mt-1">{d.body}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Text deliverables */}
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            {["caption", "product_copy", "email", "hashtags", "landing_page"].flatMap((t) => byType(t)).map((d) => {
              const Ic = ICON[d.deliverable_type] || ChatText;
              return (
                <div key={d.id} className="bg-white/[0.03] border border-white/8 rounded-lg px-3 py-2" data-testid={`del-text-${d.id}`}>
                  <div className="flex items-center justify-between"><div className="text-xs brand-text font-semibold flex items-center gap-1"><Ic size={13} /> {d.title}</div>
                    <button title="Copy" onClick={() => { navigator.clipboard.writeText(d.body); toast.success("Copied"); }} className="text-[#7a7a7a] hover:text-white"><Copy size={13} /></button></div>
                  <div className="text-xs text-[#cfcfcf] mt-1 whitespace-pre-wrap line-clamp-4">{d.body}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Campaign history */}
      <div className="card-matte p-5" data-testid="campaign-history">
        <div className="font-head font-semibold text-sm mb-3">Campaigns</div>
        {!(campaignsQ.data || []).length ? <div className="text-xs text-[#7a7a7a]">No campaigns yet — give your AI Director a command above.</div> : (
          <div className="space-y-2">
            {campaignsQ.data.map((cp) => (
              <button key={cp.id} data-testid={`campaign-row-${cp.id}`} onClick={() => openCampaign.mutate(cp.id)} className="w-full flex items-center justify-between text-left bg-white/[0.03] border border-white/8 rounded-lg px-3 py-2 hover:border-white/20">
                <div><div className="text-sm text-[#cfcfcf]">{cp.name}</div><div className="text-[10px] text-[#7a7a7a]">{(cp.created_at || "").slice(0, 10)} · {cp.objective?.slice(0, 60)}</div></div>
                <PaperPlaneTilt size={15} className="text-[#7a7a7a]" />
              </button>
            ))}
          </div>
        )}
      </div>
    </PageContainer>
  );
}
